import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArrowRight, Calendar, Trophy, Users, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import NewsSlideshow from "@/components/NewsSlideshow";
import { SocialFeed } from "@/components/SocialFeed";
import { formatMatchDate } from "@/lib/dates";

const HERO_BG = "https://images.unsplash.com/photo-1558465202-92356bf74344?crop=entropy&cs=srgb&fm=jpg&q=85";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_soccer-pro-21/artifacts/6hs5jyaw_image-removebg-preview.png";

const HomePage = () => {
  const [news, setNews] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [results, setResults] = useState([]);
  const [standings, setStandings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamsMap, setTeamsMap] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, upcomingRes, resultsRes, teamsRes, compsRes] = await Promise.all([
          axios.get(`${API}/news?limit=4`),
          // Nearest upcoming fixtures (earliest first)
          axios.get(`${API}/matches?status=scheduled&sort=earliest&limit=4`),
          // Latest results (newest completed first)
          axios.get(`${API}/matches?status=completed&sort=latest&limit=4`),
          axios.get(`${API}/teams`),
          axios.get(`${API}/competitions`)
        ]);

        setNews(newsRes.data);
        // "Our Teams" = ABFA clubs in domestic competitions only (no national / CONCACAF opponents)
        setTeams(teamsRes.data.filter(t => (t.active_competition_ids || []).length > 0 && t.team_type !== "national"));
        setCompetitions(compsRes.data);

        // Create teams map for quick lookup
        const tMap = {};
        teamsRes.data.forEach(t => { tMap[t.team_id] = t; });
        setTeamsMap(tMap);

        setFixtures(upcomingRes.data);
        setResults(resultsRes.data);

        // Get standings for first competition
        if (compsRes.data.length > 0) {
          const standingsRes = await axios.get(`${API}/standings/${compsRes.data[0].competition_id}`);
          setStandings(standingsRes.data.slice(0, 5));
        }
      } catch (error) {
        console.error("Error fetching home data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => formatMatchDate(dateStr, { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div data-testid="home-page">
      {/* Auto-rotating News + Media Slideshow — dynamic hero */}
      <NewsSlideshow />

      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <img 
            src={LOGO_URL} 
            alt="ABFA Logo" 
            className="w-32 h-32 mx-auto mb-6 animate-fade-in"
          />
          <h1 className="font-heading text-5xl md:text-7xl font-bold uppercase tracking-tight mb-4 animate-fade-in-delay-1">
            Antigua & Barbuda
          </h1>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#FFC72C] mb-6 animate-fade-in-delay-2">
            Football Association
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-8 animate-fade-in-delay-3">
            The Heart of Caribbean Football Since 1928
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-3">
            <Link to="/fixtures" className="btn-abfa-primary" data-testid="hero-fixtures-btn">
              <span>View Fixtures</span>
            </Link>
            <Link to="/competitions" className="btn-abfa-secondary border-white text-white hover:bg-white hover:text-black">
              Competitions
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats Bar */}
      <section className="bg-[#09090b] py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Trophy, label: "Competitions", value: competitions.length || 0 },
              { icon: Users, label: "Teams", value: teams.length || 0 },
              { icon: Calendar, label: "Upcoming Matches", value: fixtures.length || 0 },
              { icon: Calendar, label: "Founded", value: "1928" },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-4 text-white">
                <div className="p-3 bg-gray-800 rounded">
                  <stat.icon className="w-6 h-6 text-[#FFC72C]" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{stat.value}</p>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* News Section - Takes 2 columns */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-3xl font-bold uppercase text-[#09090b]">Latest News</h2>
                <Link to="/news" className="flex items-center gap-2 text-[#009E60] hover:text-[#008050] font-medium" data-testid="view-all-news-link">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="space-y-3">
                      <Skeleton className="h-48 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              ) : news.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {news.map((article, i) => (
                    <Link 
                      key={article.article_id} 
                      to={`/news/${article.slug || article.article_id}`}
                      className={`card-abfa group ${i === 0 ? 'md:col-span-2' : ''}`}
                      data-testid={`news-card-${i}`}
                    >
                      <div className={`${i === 0 ? 'aspect-[2/1]' : 'aspect-video'} overflow-hidden`}>
                        <img 
                          src={article.image_url || HERO_BG} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <p className="text-xs text-[#009E60] uppercase tracking-wider mb-2">{article.category}</p>
                        <h3 className={`font-heading font-bold uppercase text-[#09090b] group-hover:text-[#009E60] transition-colors ${i === 0 ? 'text-xl' : 'text-lg'}`}>
                          {article.title}
                        </h3>
                        {i === 0 && article.excerpt && (
                          <p className="text-gray-600 mt-2 text-sm">{article.excerpt}</p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-12">No news articles yet.</p>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Upcoming Fixtures */}
              <div className="bg-gray-50 p-6" data-testid="upcoming-fixtures-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold uppercase text-[#09090b]">Upcoming</h3>
                  <Link to="/fixtures" className="text-sm text-[#009E60] hover:underline">View All</Link>
                </div>
                
                {loading ? (
                  <div className="space-y-4">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
                  </div>
                ) : fixtures.length > 0 ? (
                  <div className="space-y-4">
                    {fixtures.map(match => (
                      <Link to={`/matches/${match.match_id}`} key={match.match_id} className="block hover:bg-gray-100 transition-colors bg-white p-4 border-l-4 border-[#FFC72C]" data-testid={`fixture-${match.match_id}`}>
                        <p className="text-xs text-gray-500 mb-2">{formatDate(match.match_date)} • {match.match_time || 'TBD'}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img 
                              src={teamsMap[match.home_team_id]?.logo_url || 'https://placehold.co/40x40/09090b/FFF?text=H'} 
                              alt="" 
                              className="w-8 h-8 object-contain"
                            />
                            <span className="font-medium text-sm">{teamsMap[match.home_team_id]?.short_name || 'HOME'}</span>
                          </div>
                          <span className="text-xs text-gray-400">VS</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{teamsMap[match.away_team_id]?.short_name || 'AWAY'}</span>
                            <img 
                              src={teamsMap[match.away_team_id]?.logo_url || 'https://placehold.co/40x40/09090b/FFF?text=A'} 
                              alt="" 
                              className="w-8 h-8 object-contain"
                            />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No upcoming fixtures.</p>
                )}
              </div>

              {/* Latest Results */}
              <div className="bg-gray-50 p-6" data-testid="latest-results-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold uppercase text-[#09090b]">Results</h3>
                  <Link to="/fixtures" className="text-sm text-[#009E60] hover:underline">View All</Link>
                </div>
                
                {loading ? (
                  <div className="space-y-4">
                    {[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
                  </div>
                ) : results.length > 0 ? (
                  <div className="space-y-4">
                    {results.map(match => (
                      <Link to={`/matches/${match.match_id}`} key={match.match_id} className="block hover:bg-gray-100 transition-colors bg-white p-4 border-l-4 border-[#009E60]" data-testid={`result-${match.match_id}`}>
                        <p className="text-xs text-gray-500 mb-2">{formatDate(match.match_date)}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img 
                              src={teamsMap[match.home_team_id]?.logo_url || 'https://placehold.co/40x40/09090b/FFF?text=H'} 
                              alt="" 
                              className="w-8 h-8 object-contain"
                            />
                            <span className="font-medium text-sm">{teamsMap[match.home_team_id]?.short_name || 'HOME'}</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-2 font-heading font-bold text-lg">
                              <span>{match.home_score}</span>
                              <span className="text-gray-300">-</span>
                              <span>{match.away_score}</span>
                            </div>
                            {match.shootout_home_score != null && match.shootout_away_score != null && (
                              <div className="text-[9px] font-semibold text-[#FFC72C] uppercase tracking-wider">
                                ({match.shootout_home_score}-{match.shootout_away_score} pens)
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{teamsMap[match.away_team_id]?.short_name || 'AWAY'}</span>
                            <img 
                              src={teamsMap[match.away_team_id]?.logo_url || 'https://placehold.co/40x40/09090b/FFF?text=A'} 
                              alt="" 
                              className="w-8 h-8 object-contain"
                            />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No results yet.</p>
                )}
              </div>

              {/* Standings Preview */}
              <div className="bg-gray-50 p-6" data-testid="standings-preview-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold uppercase text-[#09090b]">Standings</h3>
                  <Link to="/competitions" className="text-sm text-[#009E60] hover:underline">Full Table</Link>
                </div>
                
                {loading ? (
                  <div className="space-y-2">
                    {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                  </div>
                ) : standings.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b">
                        <th className="text-left py-2">#</th>
                        <th className="text-left py-2">Team</th>
                        <th className="text-center py-2">P</th>
                        <th className="text-center py-2">Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((s, i) => (
                        <tr key={s.team_id} className="border-b border-gray-200 hover:bg-gray-100" data-testid={`standing-row-${i}`}>
                          <td className="py-3 font-bold text-sm">{s.position}</td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <img 
                                src={teamsMap[s.team_id]?.logo_url || 'https://placehold.co/24x24/09090b/FFF?text=T'} 
                                alt="" 
                                className="w-6 h-6 object-contain"
                              />
                              <span className="font-medium text-sm">{teamsMap[s.team_id]?.short_name || 'TBD'}</span>
                            </div>
                          </td>
                          <td className="py-3 text-center text-sm text-gray-600">{s.played}</td>
                          <td className="py-3 text-center font-bold text-sm">{s.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-500 text-center py-4">No standings yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Teams */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-3xl font-bold uppercase text-[#09090b]">Our Teams</h2>
            <Link to="/teams" className="flex items-center gap-2 text-[#009E60] hover:text-[#008050] font-medium" data-testid="view-all-teams-link">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="text-center">
                  <Skeleton className="w-24 h-24 rounded-full mx-auto mb-4" />
                  <Skeleton className="h-4 w-3/4 mx-auto" />
                </div>
              ))}
            </div>
          ) : teams.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {teams.slice(0, 8).map(team => (
                <Link 
                  key={team.team_id} 
                  to={`/teams/${team.team_id}`}
                  className="group text-center p-6 bg-white hover:shadow-lg transition-all"
                  data-testid={`team-card-${team.team_id}`}
                >
                  <img 
                    src={team.logo_url || 'https://placehold.co/100x100/09090b/FFF?text=TEAM'} 
                    alt={team.name}
                    className="w-20 h-20 mx-auto mb-4 object-contain group-hover:scale-110 transition-transform"
                  />
                  <h3 className="font-heading font-bold uppercase text-[#09090b] group-hover:text-[#009E60] transition-colors">
                    {team.short_name}
                  </h3>
                  <p className="text-sm text-gray-500">{team.name}</p>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-12">No teams registered yet.</p>
          )}
        </div>
      </section>

      <SocialFeed />

      {/* CTA Section */}
      <section className="py-20 bg-[#09090b]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase text-white mb-4">
            Join the ABFA Family
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Stay updated with the latest news, fixtures, and results from Antiguan football.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-abfa-primary" data-testid="cta-contact-btn">
              <span>Contact Us</span>
            </Link>
            <Link to="/news" className="btn-abfa-secondary border-white text-white hover:bg-white hover:text-black">
              Latest News
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
